import { Trans } from "@lingui/macro";
import cx from "classnames";
import { useMemo } from "react";

import {
  TIMEFRAMES,
  TIME_AXIS_NARROW,
  TIME_AXIS_WIDE,
  type Candle,
  type MarketState,
  type Timeframe,
  num,
  usd,
} from "../terminalMock";

/** Tool-rail glyphs — placeholders for the real drawing-tool icon set. */
const TOOL_GLYPHS = ["✛", "／", "⟋", "⌗", "T", "◧", "⌾", "✎"];

const GRID_LINES = 5;
/** Vertical padding of the plot area, in px — the gridline maths needs it. */
const PLOT_PAD_TOP = 12;
/** Candles kept on very narrow screens. */
const NARROW_CANDLES = 48;

type Props = {
  market: MarketState;
  timeframe: Timeframe;
  onTimeframeChange: (timeframe: Timeframe) => void;
  leverage: number;
  /** below 640px — thins the series and tightens the gutters */
  isNarrow: boolean;
  isWide: boolean;
};

type Geometry = {
  min: number;
  max: number;
  y: (value: number) => number;
  maxVolume: number;
};

function useGeometry(candles: Candle[]): Geometry {
  return useMemo(() => {
    const max = Math.max(...candles.map((c) => c.h)) + 60;
    const min = Math.min(...candles.map((c) => c.l)) - 60;
    const maxVolume = Math.max(...candles.map((c) => c.v));

    return {
      min,
      max,
      maxVolume,
      y: (value: number) => ((max - value) / (max - min)) * 100,
    };
  }, [candles]);
}

export function ChartPanel({ market, timeframe, onTimeframeChange, leverage, isNarrow, isWide }: Props) {
  const { price, open24, clock } = market;

  /*
   * Below ~640px the full 84-candle series works out to under 3px per candle,
   * which reads as noise. Show the recent tail instead and drop the 1px gap.
   */
  const candles = useMemo(
    () => (isNarrow ? market.candles.slice(-NARROW_CANDLES) : market.candles),
    [market.candles, isNarrow]
  );
  const geometry = useGeometry(candles);

  const change = price - open24;
  const changePct = (change / open24) * 100;
  const isUp = change >= 0;
  const trendColor = isUp ? "var(--t-long)" : "var(--t-short)";

  const padLeft = isWide ? 12 : 8;
  const padRight = isWide ? 74 : isNarrow ? 46 : 54;
  const plotHeight = isWide ? 392 : 280;
  const volumeHeight = isWide ? 64 : 44;
  const candleGap = isNarrow ? "gap-0" : "gap-1";

  const gridLines = useMemo(() => {
    const lines: { value: number; offset: string; labelOffset: string }[] = [];

    for (let i = 0; i <= GRID_LINES; i++) {
      const value = geometry.max - ((geometry.max - geometry.min) / GRID_LINES) * i;
      const pct = (i / GRID_LINES) * 100;
      const shift = (pct / 100) * PLOT_PAD_TOP;

      lines.push({
        value,
        offset: `calc(${PLOT_PAD_TOP}px + ${pct}% - ${shift}px)`,
        labelOffset: `calc(${PLOT_PAD_TOP}px + ${pct}% - ${shift + 7}px)`,
      });
    }

    return lines;
  }, [geometry]);

  /* The live-price marker sits slightly compressed against the plot padding. */
  const lastTop = `calc(${PLOT_PAD_TOP}px + ${geometry.y(price).toFixed(2)}% * 0.94)`;

  const marketStats = [
    {
      label: <Trans>24h Change</Trans>,
      value: `${isUp ? "+" : ""}${num(change, 2)} / ${isUp ? "+" : ""}${changePct.toFixed(2)}%`,
      color: trendColor,
    },
    { label: <Trans>24h Volume</Trans>, value: "$1.42B", color: "var(--t-tx)" },
    { label: <Trans>Open Interest</Trans>, value: "$318.6M", color: "var(--t-tx)" },
    { label: <Trans>Funding / 8h</Trans>, value: "+0.0091%", color: "var(--t-act)" },
  ];

  const timeAxis = isWide ? TIME_AXIS_WIDE : TIME_AXIS_NARROW;

  return (
    <section className="t-panel t-anim-rise overflow-hidden">
      {/* market header */}
      <div
        className="flex flex-wrap items-center gap-26 border-b px-18 py-14"
        style={{ borderColor: "var(--t-line)" }}
      >
        <div className="flex items-center gap-11">
          <span
            className="t-display grid h-32 w-32 place-items-center rounded-full text-[17px] text-white"
            style={{ background: "var(--t-acct)" }}
          >
            B
          </span>
          <span className="t-display text-[26px] tracking-[0.5px]" style={{ color: "var(--t-deep)" }}>
            BTC–USDC
          </span>
          <span className="text-11" style={{ color: "var(--t-mut)" }}>
            ▾
          </span>
          <span
            className="rounded-full border px-10 py-3 text-11 font-semibold"
            style={{ color: "var(--t-act)", background: "var(--t-chip)", borderColor: "var(--t-mid)" }}
          >
            {leverage}×
          </span>
        </div>

        <div className="flex items-center gap-10">
          <span className="t-display t-nums text-[30px] tracking-[0.5px]" style={{ color: "var(--t-tx)" }}>
            {usd(price)}
          </span>
          <span
            className="t-anim-twinkle h-8 w-8 rounded-full"
            style={{ background: "var(--t-long)" }}
            aria-hidden="true"
          />
        </div>

        {marketStats.map((stat, index) => (
          <div key={index} className="flex flex-col gap-3">
            <span className="text-11 uppercase tracking-[0.4px]" style={{ color: "var(--t-mut)" }}>
              {stat.label}
            </span>
            <span className="t-nums text-14 font-semibold" style={{ color: stat.color }}>
              {stat.value}
            </span>
          </div>
        ))}
      </div>

      {/* toolbar */}
      <div
        className="flex items-center gap-14 overflow-x-auto border-b px-18 py-9 scrollbar-hide"
        style={{ background: "var(--t-panel2)", borderColor: "var(--t-line2)" }}
      >
        {TIMEFRAMES.map((item) => {
          const isActive = timeframe === item;

          return (
            <button
              key={item}
              type="button"
              onClick={() => onTimeframeChange(item)}
              className={cx("shrink-0 rounded-full px-11 py-5 text-[12.5px] font-semibold transition-all")}
              style={
                isActive
                  ? { background: "var(--t-act)", color: "#fff" }
                  : { background: "transparent", color: "var(--t-mut)" }
              }
            >
              {item}
            </button>
          );
        })}

        <span className="h-18 w-1 shrink-0" style={{ background: "var(--t-line)" }} />
        <span className="shrink-0 whitespace-nowrap text-[12.5px]" style={{ color: "var(--t-mut)" }}>
          <Trans>Indicators</Trans>
        </span>
        <span className="shrink-0 whitespace-nowrap text-[12.5px]" style={{ color: "var(--t-mut)" }}>
          <Trans>Compare</Trans>
        </span>

        <div className="ml-auto flex items-center gap-14 whitespace-nowrap text-12">
          <span className="t-nums" style={{ color: "var(--t-mut2)" }}>
            {clock} UTC
          </span>
          <span className="font-semibold" style={{ color: "var(--t-act)" }}>
            auto
          </span>
        </div>
      </div>

      {/* chart body */}
      <div className="flex">
        {isWide && (
          <div
            className="flex w-44 shrink-0 flex-col items-center gap-16 border-r py-16"
            style={{ background: "var(--t-panel2)", borderColor: "var(--t-line2)" }}
          >
            {TOOL_GLYPHS.map((glyph, index) => (
              <span
                key={index}
                className="t-tool-icon cursor-pointer text-15 transition-[color,transform] duration-200"
                style={{ color: "var(--t-mid)" }}
              >
                {glyph}
              </span>
            ))}
          </div>
        )}

        <div className="min-w-0 flex-1">
          {/* plot area */}
          <div
            className="relative"
            style={{ height: plotHeight, padding: `${PLOT_PAD_TOP}px ${padRight}px 0 ${padLeft}px` }}
          >
            {gridLines.map((line, index) => (
              <div
                key={`grid-${index}`}
                className="absolute border-t border-dashed"
                style={{ left: padLeft, right: padRight, top: line.offset, borderColor: "var(--t-grid)" }}
              />
            ))}

            {gridLines.map((line, index) => (
              <div
                key={`label-${index}`}
                className="t-nums absolute right-8 text-11"
                style={{ top: line.labelOffset, color: "var(--t-mut2)" }}
              >
                {num(line.value, 0)}
              </div>
            ))}

            <div
              className={cx("absolute flex items-stretch", candleGap)}
              style={{ top: PLOT_PAD_TOP, right: padRight, bottom: 0, left: padLeft }}
            >
              {candles.map((candle, index) => {
                const isGreen = candle.c >= candle.o;
                const color = isGreen ? "var(--t-long)" : "var(--t-short)";
                const top = geometry.y(Math.max(candle.o, candle.c));
                const bottom = geometry.y(Math.min(candle.o, candle.c));

                return (
                  <div key={index} className="t-anim-grow relative flex-1">
                    <span
                      className="absolute left-1/2 w-1 -translate-x-1/2 opacity-75"
                      style={{
                        background: color,
                        top: `${geometry.y(candle.h).toFixed(2)}%`,
                        height: `${(geometry.y(candle.l) - geometry.y(candle.h)).toFixed(2)}%`,
                      }}
                    />
                    <span
                      className="absolute inset-x-0 rounded-1"
                      style={{
                        background: color,
                        top: `${top.toFixed(2)}%`,
                        height: `${Math.max(0.6, bottom - top).toFixed(2)}%`,
                      }}
                    />
                  </div>
                );
              })}
            </div>

            <div
              className="absolute border-t border-dashed transition-[top] duration-[600ms] ease-out"
              style={{ left: padLeft, right: padRight, top: lastTop, borderColor: trendColor }}
            />
            <div
              className="t-nums absolute right-4 rounded-4 px-6 py-2 text-11 font-bold text-white transition-[top] duration-[600ms] ease-out"
              style={{ top: `calc(${lastTop} - 10px)`, background: trendColor }}
            >
              {usd(price)}
            </div>
          </div>

          {/* volume strip */}
          <div
            className={cx("relative flex items-end", candleGap)}
            style={{ height: volumeHeight, padding: `6px ${padRight}px 8px ${padLeft}px` }}
          >
            {candles.map((candle, index) => (
              <span
                key={index}
                className="t-anim-grow-slow flex-1 rounded-t-1"
                style={{
                  background: candle.c >= candle.o ? "var(--t-vol-up)" : "var(--t-vol-down)",
                  height: `${((candle.v / geometry.maxVolume) * 100).toFixed(1)}%`,
                }}
              />
            ))}
          </div>

          {/* time axis */}
          <div
            className="t-nums flex justify-between overflow-hidden text-[10.5px]"
            style={{ padding: `0 ${padRight}px 10px ${padLeft}px`, color: "var(--t-mut2)" }}
          >
            {timeAxis.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
